import {
  Component,
  OnInit,
  OnDestroy,
  AfterViewInit,
  ViewChild,
  ViewChildren,
  QueryList,
  ElementRef,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from "@angular/core";
import { GoldenLayoutPopout } from "../golden-layout/golden-layout-popout";
import { ProfileUpdateRequestImpl, ProfileSettingImpl, Long } from "shared/cubiq-model-multi-asset";
import {
  ActiveUser,
  ProfilePromiseActionService,
  ActiveUserProfilePromiseService,
  SystemConfigPromiseService,
  Roles,
  ProfileSettingKeys,
} from "shared/cubiq-services";
import {
  BfxUserSettingsService,
  BfxUserSettingsComponentState,
  BfxUserSettingsCategoryIndex,
} from "./bfx-user-settings.service";
import {
  BfxUserSettingsPageComponent,
  IBfxUserSettingsPageSubComponent,
  IBfxUserSettingsPageComponent,
  BfxUserSettingsValidityState,
} from "./bfx-user-settings-page.component";
import { BfxUserProfileService } from "../bfx-common/services/bfx-user-profile.service";

export class BfxUserSettingsTabConfig {
  public name: string;
  public disabled: boolean;

  constructor() {}
}

const VALID_PROMISE: Promise<BfxUserSettingsValidityState> =
  new Promise<BfxUserSettingsValidityState>((resolve, reject) => {
    resolve(BfxUserSettingsValidityState.VALID);
  });
const EMPTY_PROMISE: Promise<void> = new Promise<void>((resolve, reject) => {
  resolve();
});

const TAB_TITLE_GENERAL = "General";
const TAB_TITLE_ORDER_BLOTTER_LADDER = "Order Blotter/Ladder";
const TAB_TITLE_TRADE_BLOTTER = "Client Trade Blotter";
const TAB_TITLE_BMARK_SUMMARY = "Benchmark Summary";
const TAB_TITLE_ORDER_ENTRY = "Order Entry";
const TAB_TITLE_NOTIFICATIONS = "Notifications";
const TAB_TITLE_PANIC_CONTROLS = "Panic Controls";

export const USERSETTINGS_CATEGORY_BMARKSUMMARY = "BmarkSummary";

export const USER_SETTINGS_TABS: BfxUserSettingsTabConfig[] = [
  { name: TAB_TITLE_GENERAL, disabled: false },
  { name: TAB_TITLE_ORDER_BLOTTER_LADDER, disabled: false },
  { name: TAB_TITLE_BMARK_SUMMARY, disabled: false },
  { name: TAB_TITLE_ORDER_ENTRY, disabled: false },
  // {name: "Reporting", disabled: true},
  { name: TAB_TITLE_NOTIFICATIONS, disabled: false },
  // {name: "Acceptance", disabled: true},
  { name: TAB_TITLE_PANIC_CONTROLS, disabled: false },
];
const delay = function (t, val) {
  return new Promise((resolve) => {
    setTimeout(resolve.bind(null, val), t);
  });
};
const raceAll = function (promises, timeoutTime, timeoutVal) {
  return Promise.all(
    promises.map((p) => {
      return Promise.race([p, delay(timeoutTime, timeoutVal)]);
    }),
  );
};
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

@Component({
  selector: "bfx-user-settings",
  templateUrl: "./html/bfx-user-settings.html",
  standalone: false,
})
export class BfxUserSettingsComponent implements OnDestroy, OnInit, AfterViewInit {
  public dividerPosition: number = 180;
  public dividerDragInProgress: boolean = false;
  public tabNames: BfxUserSettingsTabConfig[] = USER_SETTINGS_TABS;
  public activeUser: ActiveUser;
  public activeCategory: number = 0;
  public activeSubCategory: number = 0;
  public categoryValid: Array<BfxUserSettingsValidityState> = [];
  public saveDisabled: boolean = true;
  public revertDisabled: boolean = true;
  public isLoading: boolean = true;
  public viewInitComplete: boolean = false;

  // Wrap Enum as a property for Template
  public BfxUserSettingsCategoryIndex = BfxUserSettingsCategoryIndex;
  public BfxUserSettingsValidityState = BfxUserSettingsValidityState;

  @ViewChild("rootElement", { static: true }) rootElement: ElementRef;

  @ViewChild("tableElement", { static: true }) element: ElementRef;

  @ViewChildren("settingComponent")
  children: QueryList<BfxUserSettingsPageComponent>;

  private benchmarksEnabled: boolean = true;
  private panicControlEnabled: boolean = true;
  public parentPopup: GoldenLayoutPopout;

  constructor(
    public userSettingsService: BfxUserSettingsService,
    public systemConfigService: SystemConfigPromiseService,
    public profileService: ProfilePromiseActionService,
    public activeUserService: ActiveUserProfilePromiseService,
    public componentElement: ElementRef,
    public cd: ChangeDetectorRef,
    private userProfileService: BfxUserProfileService,
  ) {}

  ngOnInit(): void {
    let componentState: BfxUserSettingsComponent = this.userSettingsService.getComponentState();
    this.activeCategory = componentState.activeCategory;
    this.activeSubCategory = componentState.activeSubCategory;

    USER_SETTINGS_TABS.forEach((cfg) => {
      this.categoryValid.push(BfxUserSettingsValidityState.VALID);
    });

    this.systemConfigService.hasRole(Roles.ADMIN_PANIC).then((value: boolean) => {
      logger.out.info(`BfxPanicControlsComponent: ADMIN_PANIC ${value}`);
      if (value === false) {
        this.panicControlEnabled = false;
      }
    });

    this.systemConfigService.areBenchmarksEnabled().then((result: boolean) => {
      if (!result) {
        logger.out.info(
          `BfxUserSettingsComponent: Removing Benchmark Summary as Bmarks not enabled`,
        );
        this.benchmarksEnabled = false;
      }
    });
  }

  ngOnDestroy() {
    this.viewInitComplete = false;
  }

  ngAfterViewInit() {
    //Called after ngAfterContentInit when the component's view has been initialized. Applies to components only.
    //Add 'implements AfterViewInit' to the class.
    this.activeUserService.getActiveUserData().then((data: ActiveUser) => {
      this.activeUser = data;

      setTimeout(() => {
        this.viewInitComplete = true;
        this.isLoading = false;
      }, 200);

      this.children.toArray().forEach((categoryComponent: BfxUserSettingsPageComponent) => {
        categoryComponent.parentComponent = this;

        if (categoryComponent.subComponents == Object(categoryComponent.subComponents)) {
          categoryComponent.subComponents
            .toArray()
            .forEach(
              (subComponent: IBfxUserSettingsPageSubComponent<IBfxUserSettingsPageComponent>) => {
                subComponent.categoryComponent = categoryComponent;
                subComponent.parentComponent = this;
              },
            );
        }
      });

      this.children.toArray().forEach((categoryComponent: BfxUserSettingsPageComponent) => {
        if (categoryComponent.initialize) {
          // subComponent initialize must be called during categoryComponent.initialize();
          setImmediate(() => {
            categoryComponent.initialize();
            this.cd.detectChanges();
          });
        }
      });
    });
  }

  setParentPopup(parentPopup: GoldenLayoutPopout) {
    this.parentPopup = parentPopup;
    this.parentPopup.setShowDock(false);
  }

  public dividerStart(event: any): void {
    this.dividerDragInProgress = true;
  }

  public dividerDrag(event: any): void {
    if (this.dividerDragInProgress) {
      this.dividerPosition = event.clientX - this.element.nativeElement.offsetParent.offsetLeft;
    }
  }

  public dividerEnd(event: any): void {
    this.dividerDragInProgress = false;
  }

  public resize(): void {
    this.updateComponentState();
  }

  /**
   * Callback that determines if this popup should be closed or not
   */
  public onClose(): boolean {
    this.updateComponentState();
    return true;
  }

  public save(): void {
    if (!this.saveDisabled) {
      this.saveDisabled = true;
      this.revertDisabled = true;
      this.cd.detectChanges();

      this.validate(this, true).then((valid: BfxUserSettingsValidityState) => {
        if (valid === BfxUserSettingsValidityState.VALID) {
          this.profileService
            .createUserSettingsProfileUpdateRequest()
            .then((request: ProfileUpdateRequestImpl) => {
              this.children
                .toArray()
                .forEach((value: BfxUserSettingsPageComponent, index: number) => {
                  if (value.save) {
                    value.save(request);
                  }
                });

              let settingsList: Array<ProfileSettingImpl> = this.createSettingList(request);
              let updateUserPreferences: boolean = this.isUserPreferencesModified(request);
              if (updateUserPreferences) settingsList.push(this.createUserPreferences(request));
              if (settingsList.length > 0) {
                this.profileService
                  .updateProfile(settingsList, updateUserPreferences)
                  .then((value: any) => {
                    this.children
                      .toArray()
                      .forEach((value: BfxUserSettingsPageComponent, index: number) => {
                        if (value.postSave) {
                          value.postSave(request);
                        }
                      });

                    this.activeUserService.setActiveUserData(this.activeUser);
                    this.saveDisabled = true;
                    this.revertDisabled = true;
                  })
                  .catch((error: any) => {
                    this.saveDisabled = false;
                    this.revertDisabled = false;
                  });
              } else {
                logger.out.info(
                  `${this.constructor.name}: no saving required : ` +
                    settingsList.length +
                    ` settings modified %O`,
                );
                this.saveDisabled = true;
                this.revertDisabled = true;
              }
            });
        } else {
          this.saveDisabled = true;
          this.revertDisabled = true;
        }
      });
    }
  }

  public revert(): void {
    if (!this.revertDisabled) {
      this.saveDisabled = true;
      this.revertDisabled = true;
      this.isLoading = true;

      this.activeUserService.getActiveUserData().then((data: ActiveUser) => {
        let children = this.children.toArray();
        this.activeUser = data;

        let promises: Array<Promise<void>> = children.map((child: BfxUserSettingsPageComponent) => {
          return new Promise<void>((res) => {
            setTimeout(() => {
              (child.reset ? child.reset() : EMPTY_PROMISE).then(() => {
                res();
              });
            }, 4);
          });
        });

        raceAll(promises, 300, null).then(() => {
          this.cd.detectChanges();

          children.forEach((child: BfxUserSettingsPageComponent, index) => {
            this.validate(child, true).then(() => {
              if (child.postReset) {
                child.postReset();
              }
              this.revertDisabled = true;
              this.saveDisabled = true;
            });
          });
          this.isLoading = false;
        });
      });
    }
  }

  public validate(
    validateComponent: BfxUserSettingsComponent | BfxUserSettingsPageComponent,
    saveDisabled: boolean = false,
    revertDisabled: boolean = false,
  ): Promise<BfxUserSettingsValidityState> {
    //logger.out.info(`${this.constructor.name}.validate() saveDisabled:${saveDisabled}`);
    return new Promise<BfxUserSettingsValidityState>((resolve, reject) => {
      let saveable: boolean = true;
      let globalState: BfxUserSettingsValidityState = BfxUserSettingsValidityState.VALID;
      let componentIndex = -1;
      let promises: Array<Promise<BfxUserSettingsValidityState>> = [];

      if (validateComponent != null && validateComponent !== this) {
        componentIndex = this.children.toArray().findIndex((child) => child === validateComponent);

        if (componentIndex != -1) {
          let child = this.children.toArray()[componentIndex];
          promises = [child.validate ? child.validate() : VALID_PROMISE];
        } else {
          promises = this.children.toArray().map((child: BfxUserSettingsPageComponent) => {
            return child.validate ? child.validate() : VALID_PROMISE;
          });
        }
      }
      (raceAll(promises, 300, BfxUserSettingsValidityState.VALIDATE_TIMEOUT) as any).then(
        (states: Array<BfxUserSettingsValidityState>) => {
          states.forEach((state: BfxUserSettingsValidityState, index: number) => {
            globalState =
              globalState === BfxUserSettingsValidityState.INVALID ||
              state === BfxUserSettingsValidityState.INVALID
                ? BfxUserSettingsValidityState.INVALID
                : globalState === BfxUserSettingsValidityState.ACTIVE_UNSAVEABLE ||
                    state === BfxUserSettingsValidityState.ACTIVE_UNSAVEABLE
                  ? BfxUserSettingsValidityState.ACTIVE_UNSAVEABLE
                  : BfxUserSettingsValidityState.VALID;

            //logger.out.info(`${this.constructor.name}.validate() this.categoryValid[${index}]  valid:BfxUserSettingsValidityState.${BfxUserSettingsValidityState[globalState]}`);

            if (componentIndex != -1) this.categoryValid[componentIndex] = state;
            else this.categoryValid[index] = state;
          });

          if (globalState === BfxUserSettingsValidityState.VALIDATE_TIMEOUT) {
            // previous default to BfxUserSettingsValidityState.INVALID was too strict
            logger.out.warn(`${this.constructor.name}.valdate() validation timeout`);
          }
          if (!this.isLoading) {
            this.saveDisabled =
              saveDisabled ||
              (globalState !== BfxUserSettingsValidityState.VALID &&
                globalState !== BfxUserSettingsValidityState.VALIDATE_TIMEOUT);

            this.revertDisabled = revertDisabled || false;
          }
          //logger.out.info(`${this.constructor.name}.validate() valid:BfxUserSettingsValidityState.${BfxUserSettingsValidityState[globalState]}`);
          resolve(globalState);
        },
      );
    });
  }

  public getCategoryClass(index: number): string {
    return this.categoryValid[index] ? "" : "icon-warning";
  }

  public setCategoryClass(index: number, value: BfxUserSettingsValidityState) {
    this.categoryValid[index] = value;
  }

  public isSettingPageAvailable(index: BfxUserSettingsCategoryIndex): boolean {
    let available: boolean = true;

    switch (index) {
      case BfxUserSettingsCategoryIndex.BmarkSummary:
        available = this.benchmarksEnabled;
        break;
      case BfxUserSettingsCategoryIndex.Panic:
        available = this.panicControlEnabled;
        break;
      default:
        break;
    }

    return available;
  }

  protected updateComponentState(): void {
    let currentBounds: any = this.componentElement.nativeElement.getBoundingClientRect();

    let componentState: BfxUserSettingsComponentState = new BfxUserSettingsComponentState(
      {
        x: currentBounds.left,
        y: currentBounds.top,
        width: currentBounds.width + 18,
        height: currentBounds.height + 38,
      },
      this.activeCategory,
      this.activeSubCategory,
    );

    Object.assign(this.userSettingsService.getComponentState(), componentState);

    this.updateChildren(componentState);
  }

  protected updateChildren(componentState: BfxUserSettingsComponentState): void {
    this.children.forEach((item: BfxUserSettingsPageComponent) => {
      if (item.onComponentStateChanged != null) {
        item.onComponentStateChanged(componentState);
      }
    });
  }

  private createSettingList(request: ProfileUpdateRequestImpl): Array<ProfileSettingImpl> {
    let profileSettings: Array<ProfileSettingImpl> = [];
    if (request && request.settings && Array.isArray(request.settings)) {
      request.settings.forEach((setting) => {
        if (
          this.activeUser.profileSettings &&
          this.activeUser.profileSettings.has(setting.profileSettingKey)
        ) {
          if (
            setting.profileSettingValue !==
            this.activeUser.profileSettings.get(setting.profileSettingKey)
          )
            profileSettings.push(
              this.userProfileService.createProfileSettingObject(
                setting.profileSettingKey,
                setting.profileSettingValue,
              ),
            );
        } else {
          profileSettings.push(
            this.userProfileService.createProfileSettingObject(
              setting.profileSettingKey,
              setting.profileSettingValue,
            ),
          );
        }
      });
    }
    return profileSettings;
  }
  private isDefaultPositionBookModified(request: ProfileUpdateRequestImpl): boolean {
    return (
      (Long.isLong(request.defaultPositionBookId) &&
        this.activeUser.defaultPositionBookId != null &&
        request.defaultPositionBookId.notEquals(
          Long.fromString(this.activeUser.defaultPositionBookId),
        )) ||
      request.defaultPositionBookId == null ||
      Long.isLong(request.defaultPositionBookId)
    );
  }

  private isUserPreferencesModified(request: ProfileUpdateRequestImpl): boolean {
    return (
      this.isDefaultPositionBookModified(request) ||
      request.emailAddress !== this.activeUser.emailAddress ||
      request.smsNumber !== this.activeUser.smsNumber ||
      request.sendEmailAnyOrderIFillFg !== this.activeUser.sendEmailAnyOrderIFillFg ||
      request.sendEmailMyOrdEnteredFg !== this.activeUser.sendEmailMyOrdEnteredFg ||
      request.sendEmailMyOrdExpiryFg !== this.activeUser.sendEmailMyOrdExpiryFg ||
      request.sendEmailMyOrdFillFg !== this.activeUser.sendEmailMyOrdFillFg ||
      request.sendSMSAnyOrderIFillFg !== this.activeUser.sendSMSAnyOrderIFillFg ||
      request.sendSMSMyOrdEnteredFg !== this.activeUser.sendSMSMyOrdEnteredFg ||
      request.sendSMSMyOrdExpiryFg !== this.activeUser.sendSMSMyOrdExpiryFg ||
      request.sendSMSMyOrdFillFg !== this.activeUser.sendSMSMyOrdFillFg ||
      request.sendEmailMyOrdModifyFg !== this.activeUser.sendEmailMyOrdModifiedFg ||
      request.sendEmailMyOrdCancelFg !== this.activeUser.sendEmailMyOrdCanceledFg ||
      request.sendSMSMyOrdModifyFg !== this.activeUser.sendSMSMyOrdModifiedFg ||
      request.sendSMSMyOrdCancelFg !== this.activeUser.sendSMSMyOrdCanceledFg
    );
  }

  private createUserPreferences(request: ProfileUpdateRequestImpl): ProfileSettingImpl {
    let userPreferences: ProfileSettingImpl = new ProfileSettingImpl();
    userPreferences.profileSettingKey = ProfileSettingKeys.userPreferences;
    let preferencesValue: any = {
      defaultPositionBookId:
        request.defaultPositionBookId !== null ? request.defaultPositionBookId.toString() : null,
      emailAddress: request.emailAddress,
      smsNumber: request.smsNumber,
      sendEmailAnyOrderIFillFg: request.sendEmailAnyOrderIFillFg,
      sendEmailMyOrdEnteredFg: request.sendEmailMyOrdEnteredFg,
      sendEmailMyOrdExpiryFg: request.sendEmailMyOrdExpiryFg,
      sendEmailMyOrdFillFg: request.sendEmailMyOrdFillFg,
      sendSMSAnyOrderIFillFg: request.sendSMSAnyOrderIFillFg,
      sendSMSMyOrdEnteredFg: request.sendSMSMyOrdEnteredFg,
      sendSMSMyOrdExpiryFg: request.sendSMSMyOrdExpiryFg,
      sendSMSMyOrdFillFg: request.sendSMSMyOrdFillFg,
      sendEmailMyOrdModifyFg: request.sendEmailMyOrdModifyFg,
      sendEmailMyOrdCancelFg: request.sendEmailMyOrdCancelFg,
      sendSMSMyOrdModifyFg: request.sendSMSMyOrdModifyFg,
      sendSMSMyOrdCancelFg: request.sendSMSMyOrdCancelFg,
    };
    userPreferences.profileSettingValue = JSON.stringify(preferencesValue);

    return userPreferences;
  }

  public onComponentStateChanged(componentState: BfxUserSettingsComponentState): void {}
}
